// Diving for shipwreck loot and shellfish pearls.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.
//
// Another subsystem with no assigned step (see world/weather.ts's header
// for why it gets its own file instead of living in main.ts). Wreck
// siting/streaming lives in world/chunks.ts (it's world-gen, like
// villages); this module only owns the dive mechanic itself, taking the
// targeted wreck as a plain object parameter.

import * as THREE from 'three';
import { ENV } from '@world/weather';
import { cast, endCast } from '@systems/fishing';
import { fisher, player } from '@entities/player';
import { boat, inBoat, spawnSplash } from '@systems/building';
import { addCoins, addPearls } from '@economy/market';

export interface WreckSite {
  ok: boolean;
  q: number;
  r: number;
  x: number;
  z: number;
  mesh: THREE.Group | null;
  // Surface ripple, only on wrecks shallow enough for the mast to break
  // through. Streamed in and out alongside `mesh`.
  ripple?: THREE.Group | null;
  loot: number;
}

export interface DiveState {
  active: boolean;
  timer: number;
  kind: 'wreck' | 'shell' | null;
  wreck: WreckSite | null;
}

export const dive: DiveState = { active: false, timer: 0, kind: null, wreck: null };

export function diveBlockReason(): string | null {
  if (ENV.weather === 'Storm') return '⛈️ Too stormy to dive!';
  if (ENV.night && !ENV.fullMoon) return '🌙 Too dark to dive.';
  return null;
}

// Bubbles rise over the actual dive site — the wreck itself for a wreck
// dive (which can be a couple hexes from where the boat is anchored), the
// boat's own position for a shellfish dive from the boat (no separate
// site object), or the player's own position for a shellfish dive while
// wading (no boat involved at all) — nudged clear of the boat, when one's
// actually part of this dive, so the hull never sits on top of them: away
// along the boat->site line when they're apart, or off to the boat's
// side when the site *is* the boat.
const BUBBLE_CLEARANCE = 0.4;

function bubbleOrigin(wreck: WreckSite | null): { x: number; z: number } {
  const site = wreck ? { x: wreck.x, z: wreck.z }
    : inBoat && boat ? { x: boat.x, z: boat.z }
    : { x: player.x, z: player.z };
  if (!boat || (!wreck && !inBoat)) return site;
  const dx = site.x - boat.x, dz = site.z - boat.z;
  const dist = Math.hypot(dx, dz);
  const [ox, oz] = dist > 0.05
    ? [dx / dist, dz / dist]
    : [Math.cos(boat.heading + Math.PI / 2), Math.sin(boat.heading + Math.PI / 2)];
  return { x: site.x + ox * BUBBLE_CLEARANCE, z: site.z + oz * BUBBLE_CLEARANCE };
}

export function startDive(kind: 'wreck' | 'shell', wreck: WreckSite | null): { success: boolean; reason?: string } {
  const block = diveBlockReason();
  if (block) return { success: false, reason: block };
  if (cast.active) endCast();
  dive.active = true;
  dive.timer = 3.2;
  dive.kind = kind;
  dive.wreck = wreck;
  fisher.visible = false;
  const origin = bubbleOrigin(wreck);
  spawnSplash(origin.x, origin.z);
  return { success: true };
}

export type DiveResult =
  | { kind: 'small-cache'; amount: number; cleaned: boolean }
  | { kind: 'pearl'; cleaned: boolean }
  | { kind: 'koban'; amount: number; cleaned: boolean }
  | { kind: 'wreck-empty' }
  | { kind: 'clam-pearl' }
  | { kind: 'clam-empty' };

function resolveDive(): DiveResult {
  fisher.visible = true;
  const origin = bubbleOrigin(dive.wreck);
  spawnSplash(origin.x, origin.z);
  let result: DiveResult;
  if (dive.kind === 'wreck' && dive.wreck) {
    const wk = dive.wreck;
    if (wk.loot > 0) {
      wk.loot--;
      const cleaned = wk.loot === 0;
      const roll = Math.random();
      if (roll < 0.5) {
        const c2 = 6 + Math.floor(Math.random() * 12);
        addCoins(c2);
        result = { kind: 'small-cache', amount: c2, cleaned };
      } else if (roll < 0.75) {
        addPearls(1);
        result = { kind: 'pearl', cleaned };
      } else {
        const c2 = 20 + Math.floor(Math.random() * 15);
        addCoins(c2);
        result = { kind: 'koban', amount: c2, cleaned };
      }
    } else {
      result = { kind: 'wreck-empty' };
    }
  } else {
    if (Math.random() < 0.35) {
      addPearls(1);
      result = { kind: 'clam-pearl' };
    } else {
      result = { kind: 'clam-empty' };
    }
  }
  dive.active = false;
  return result;
}

export function updateDive(dt: number): DiveResult | null {
  if (!dive.active) return null;
  dive.timer -= dt;
  if (Math.random() < dt * 2.5) {
    const origin = bubbleOrigin(dive.wreck);
    spawnSplash(origin.x + (Math.random() - 0.5) * 0.5, origin.z + (Math.random() - 0.5) * 0.5);
  }
  if (dive.timer <= 0) return resolveDive();
  return null;
}
